import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef
} from 'react';

import { useOrbitSessionRecord } from 'api/orbit';

import { useOrbitContext } from '../OrbitContext';
import {
  leaveOrbitNewChat,
  ORBIT_NEW_CHAT_KEY,
  orbitDemoChatKey,
  setOrbitPendingWorkspace,
  useOrbitPendingWorkspaces
} from '../orbitPendingWorkspace';
import { OrbitWorkspaceTag } from '../types';
import useOrbitChatActions from '../useOrbitChatActions';

export interface IOrbitWorkspaceSelection {
  /** The open chat: its session id, the new-chat draft, or a demo. */
  chatKey: string;
  /** The open chat's Workspace: one waiting to be filed, else the server's. */
  workspace: OrbitWorkspaceTag | null;
  /** Files a chat the server lists at once; any other chat keeps it until an answer lists it. */
  choose: (tag: OrbitWorkspaceTag | null) => void;
}

/**
 * The full page's open chat and its Workspace; `demoId` is the open demo chat, if any. A new chat
 * starts with none. Its choice moves to the session its first question opens; leaving it any other
 * way, or leaving the page, drops it.
 */
const useOrbitWorkspaceSelection = (demoId: string | null): IOrbitWorkspaceSelection => {
  const { sessionId, activeTurnId } = useOrbitContext();
  const pending = useOrbitPendingWorkspaces();
  const record = useOrbitSessionRecord(demoId === null ? sessionId : null);
  const { file } = useOrbitChatActions();
  const chatKey = demoId !== null ? orbitDemoChatKey(demoId) : sessionId ?? ORBIT_NEW_CHAT_KEY;
  const previousKey = useRef(chatKey);

  // Before paint, so no frame draws one chat with another's Workspace.
  useLayoutEffect(() => {
    const previous = previousKey.current;
    previousKey.current = chatKey;
    if (previous === chatKey || previous !== ORBIT_NEW_CHAT_KEY) {
      return;
    }
    const askedHere = demoId === null && sessionId !== null && activeTurnId !== null;
    leaveOrbitNewChat(askedHere ? chatKey : null);
  }, [chatKey, demoId, sessionId, activeTurnId]);

  useEffect(() => () => leaveOrbitNewChat(null), []);

  const choose = useCallback(
    (tag: OrbitWorkspaceTag | null) => {
      if (record !== undefined) {
        setOrbitPendingWorkspace(chatKey, null);
        file(record, tag);
        return;
      }
      setOrbitPendingWorkspace(chatKey, tag);
    },
    [chatKey, record, file]
  );

  const workspace = pending[chatKey] ?? record?.workspace ?? null;

  return useMemo(() => ({ chatKey, workspace, choose }), [chatKey, workspace, choose]);
};

export const OrbitWorkspaceSelectionContext = createContext<IOrbitWorkspaceSelection | null>(null);

export const useOrbitWorkspaces = (): IOrbitWorkspaceSelection => {
  const value = useContext(OrbitWorkspaceSelectionContext);

  if (value === null) {
    throw new Error('useOrbitWorkspaces must be used within the Orbit full page');
  }

  return value;
};

export default useOrbitWorkspaceSelection;
